import { Icon, Menu } from 'antd'
import SubMenu from 'antd/lib/menu/SubMenu'
import { hasAccesPermits } from 'core'
import React, { useMemo } from 'react'
import * as ScreenPermissions from 'core/EventActions'
import { Filler } from 'core/screen-util'

/// new england iso
const NYISOMenu = ({
  onSelect,
  menuTheme,
  currentPath,
  envConfig,
  actionPermits,
  userInfo,
  logout,
  apiTargetInfo,
}) => {
  const { authGodMode } = envConfig

  const hasAdminPermission = useMemo(
    _ => {
      const adminMenu =
        authGodMode ||
        hasAccesPermits({
          authGodMode,
          actionPermits,
          eventActions: [
            ScreenPermissions.IT_REGISTRATION,
            ScreenPermissions.CHANGE_PERMISSIONS,
            ScreenPermissions.ACCESS_PERMISSIONS,
          ],
          matchAny: true,
        })
      return adminMenu
    },
    [actionPermits, authGodMode]
  )
  const hasNYISOIIFOPermission = useMemo(
    _ =>
      hasAccesPermits({
        authGodMode,
        actionPermits,
        eventActions: [ScreenPermissions.NYISO_IIFO],
      }),
    [actionPermits, authGodMode]
  )

  const hasNYISOPLWPermission = useMemo(
    _ =>
      hasAccesPermits({
        authGodMode,
        actionPermits,
        eventActions: [ScreenPermissions.NYISO_PLW],
      }),
    [actionPermits, authGodMode]
  )

  const hasNYISOMarsParameterPermission = useMemo(
    _ =>
      hasAccesPermits({
        authGodMode,
        actionPermits,
        eventActions: [ScreenPermissions.NYISO_MARS_PARAMETERS],
      }),
    [actionPermits, authGodMode]
  )

  const hasNYISOSupportDataPermission =
    hasNYISOIIFOPermission ||
    hasNYISOPLWPermission ||
    hasNYISOMarsParameterPermission

  return (
    <Menu
      mode="horizontal"
      onSelect={onSelect}
      theme={menuTheme}
      selectedKeys={[currentPath]}
      style={{ height: '40px', width: '100%', display: 'flex' }}
    >
      {hasAdminPermission && (
        <SubMenu
          key="admin"
          title={
            <span>
              <Icon
                type="user-add"
                style={{ fontSize: '20px', fontWeight: 'bold' }}
              />
              <span>Administration</span>
            </span>
          }
          style={{
            height: '64px',
            lineHeight: '64px',
            width: hasNYISOSupportDataPermission ? '190px' : '150px',
          }}
        >
          <SubMenu key="secaccessadmin" title="Security Access">
            <Menu.Item exact key="admin/role">
              Roles
            </Menu.Item>
            <Menu.Item exact key="admin/users">
              Users
            </Menu.Item>
          </SubMenu>
          <SubMenu title="Unit Configuration">
            <Menu.Item exact key="admin/company">
              Company
            </Menu.Item>
            <Menu.Item exact key="admin/global-unit-reassignment">
              Global Unit Re-Assignment
            </Menu.Item>
            <Menu.Item exact key="admin/ownership-access">
              Ownership Access
            </Menu.Item>
            <Menu.Item exact key="admin/unit">
              Units
            </Menu.Item>
            <Menu.Item exact key="admin/unit-group">
              Unit Groups
            </Menu.Item>
            <Menu.Item exact key="admin/utility-operator">
              Utility/Operator
            </Menu.Item>
          </SubMenu>
          <SubMenu title="Lock Management">
            <Menu.Item exact key="admin/monthlylock">
              Monthly Lock Management
            </Menu.Item>
            <Menu.Item exact key="admin/unitlock">
              Unit Lock Release
            </Menu.Item>
          </SubMenu>
          <SubMenu title="Configuration">
            <Menu.Item exact key="admin/cause-code-mgmt">
              Cause Code Management
            </Menu.Item>
            <Menu.Item exact key="admin/gads-error-mgmt">
              Error Management
            </Menu.Item>
            <Menu.Item exact key="admin/process-log">
              Process Log
            </Menu.Item>
            <Menu.Item exact key="admin/system-component-mgmt">
              System/Component Management
            </Menu.Item>
            <Menu.Item exact key="admin/system-configuration">
              System Configuration
            </Menu.Item>
          </SubMenu>
          {hasNYISOSupportDataPermission && (
            <SubMenu title="NYISO Support Data">
              {hasNYISOIIFOPermission && (
                <Menu.Item exact key="admin/iifo-outage">
                  IIFO Outage
                </Menu.Item>
              )}
              {hasNYISOPLWPermission && (
                <Menu.Item exact key="admin/iifo-plw">
                  Peak Load Window
                </Menu.Item>
              )}
              {hasNYISOMarsParameterPermission && (
                <Menu.Item exact key="admin/mars-report-param">
                  MARS Report Parameters
                </Menu.Item>
              )}
            </SubMenu>
          )}
        </SubMenu>
      )}
      <SubMenu
        title={
          <span>
            <Icon
              type="link"
              style={{ fontSize: '20px', fontWeight: 'bold' }}
            />
            <span>Operation Data</span>
          </span>
        }
        style={{ height: '64px', lineHeight: '64px', width: '150px' }}
      >
        {(authGodMode ||
          hasAccesPermits({
            authGodMode,
            actionPermits,
            eventActions: [ScreenPermissions.MONTHLY_OPERATION_VIEW],
            matchAny: true,
          })) && (
          <Menu.Item key="monthlyview">Monthly Operation View</Menu.Item>
        )}
        {(authGodMode ||
          hasAccesPermits({
            authGodMode,
            actionPermits,
            eventActions: [ScreenPermissions.NERC_FILE_CREATION],
            matchAny: true,
          })) && (
          <Menu.Item key="nerc-file-creation-antd">
            NERC File Creation
          </Menu.Item>
        )}
        {(authGodMode ||
          hasAccesPermits({
            authGodMode,
            actionPermits,
            eventActions: [ScreenPermissions.CARDDATA_IMPORT],
            matchAny: true,
          })) && <Menu.Item key="data-import-antd"> Data Import</Menu.Item>}
      </SubMenu>
      {(authGodMode ||
        hasAccesPermits({
          authGodMode,
          actionPermits,
          eventActions: [
            ScreenPermissions.VALIDATION_RUNCHECK,
            ScreenPermissions.VALIDATION_LOG,
            ScreenPermissions.VALIDATION_MONTHLY,
            ScreenPermissions.VALIDATION_EVENT_PENDING,
            ScreenPermissions.VALIDATION_EVENT_PENDING_APPROVAL,
          ],
          matchAny: true,
        })) && (
        <SubMenu
          title={
            <span>
              <Icon
                type="bulb"
                style={{ fontSize: '20px', fontWeight: 'bold' }}
              />
              <span>Level 2 Validation</span>
            </span>
          }
          style={{ height: '64px', lineHeight: '64px', width: '170px' }}
        >
          <Menu.Item key="level-2-validation-tab">Validation</Menu.Item>
          <Menu.Item key="level-2-validation-status-antd">
            Validation Status
          </Menu.Item>
        </SubMenu>
      )}
      <SubMenu
        title={
          <span>
            <Icon
              type="credit-card"
              style={{ fontSize: '20px', fontWeight: 'bold' }}
            />
            <span>Card Data</span>
          </span>
        }
        style={{ width: '130px', height: '64px', lineHeight: '64px' }}
      >
        {hasAccesPermits({
          authGodMode,
          actionPermits,
          eventActions: [
            ScreenPermissions.CARD95_ADMIN,
            ScreenPermissions.CARD95_VIEWER,
          ],
          matchAny: true,
        }) && (
          <Menu.Item key="card95-performance-data-antd">
            Card95 - Performance Data
          </Menu.Item>
        )}
        {hasAccesPermits({
          authGodMode,
          actionPermits,
          eventActions: [
            ScreenPermissions.CARD97_ADMIN,
            ScreenPermissions.CARD97_VIEWER,
          ],
          matchAny: true,
        }) && (
          <Menu.Item key="card97-event-data"> Card97 - Event Data</Menu.Item>
        )}
        {hasAccesPermits({
          authGodMode,
          actionPermits,
          eventActions: [
            // ScreenPermissions.CARD_HISTORY_VIEWER,
            ScreenPermissions.CARD95_HISTORY_VIEWER,
            ScreenPermissions.CARD97_HISTORY_VIEWER,
            ScreenPermissions.CARD99_HISTORY_VIEWER,
          ],
          matchAny: true,
        }) && (
          <Menu.Item key="card-history-data-antd"> Card History Data</Menu.Item>
        )}
      </SubMenu>
      <SubMenu
        title={
          <span>
            <Icon
              type="audit"
              style={{
                height: '64px',
                lineHeight: '64px',
                fontSize: '20px',
                fontWeight: 'bold',
              }}
            />
            <span>Reports</span>
          </span>
        }
        style={{ width: '100px', height: '64px', lineHeight: '64px' }}
      >
        {(authGodMode ||
          hasAccesPermits({
            authGodMode,
            actionPermits,
            eventActions: [
              ScreenPermissions.REPORT_ADHOCK_ACCESS_PDF,
              ScreenPermissions.REPORT_ADHOCK_ACCESS_EXCEL,
            ],
            matchAny: true,
          })) && <Menu.Item key="adhoc-report">Ad Hoc Report</Menu.Item>}
        {(authGodMode ||
          hasAccesPermits({
            authGodMode,
            actionPermits,
            eventActions: [ScreenPermissions.REPORT_ACCESS],
            matchAny: true,
          })) && [
          <Menu.Item key="reportantd">Reports</Menu.Item>,
          <Menu.Item key="completedreports">Completed Reports</Menu.Item>,
        ]}
      </SubMenu>
      <SubMenu
        title={
          <span>
            <Icon
              type="tool"
              style={{
                height: '64px',
                lineHeight: '64px',
                fontSize: '20px',
                fontWeight: 'bold',
              }}
            />
            Tools
          </span>
        }
        style={{ width: '120px', height: '64px', lineHeight: '64px' }}
      >
        <Menu.Item exact key="admin/causecodelookup">
          Cause Code Lookup
        </Menu.Item>
        <Menu.Item exact key="admin/eventduracalculator">
          Event Duration Calculator
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item key="guide">Guide</Menu.Item>
        {/* <Menu.Item
          key={useHistory().location.pathname.substring(
            1,
            useHistory().location.pathname.length
          )}
          style={{ backgroundColor: '#001529' }}
        >
           <a
            href={apiTargetInfo.baseUrl + '/utils/guide?id=manual'}
            target="_self"
            style={{ backgroundColor: '#001529' }}
          > 
            Guide
           </a> 
        </Menu.Item> */}
      </SubMenu>
      <Filler />
      <SubMenu
        title={
          <Icon
            type="user"
            style={{
              height: '64px',
              lineHeight: '64px',
              fontSize: '20px',
              fontWeight: 'bold',
            }}
          />
        }
        style={{
          width: '50px',
          height: '64px',
          lineHeight: '64px',
          marginRight: '50px',
        }}
      >
        <Menu.Item disabled>
          <div style={{ color: 'white' }}>@{userInfo.userName}</div>
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item key="versionsnyiso">Versions</Menu.Item>
        <Menu.Divider />
        <Menu.Item key="/">
          <a
            href="#"
            // onClick={logout}
            onClick={e => logout(e, apiTargetInfo)}
            style={{ marginRight: '20px', color: '#fff' }}
          >
            Log out
          </a>
        </Menu.Item>
      </SubMenu>
    </Menu>
  )
}

export default NYISOMenu
